import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { useCurrentCabinet } from '@/hooks/cabinet/useCabinetCore';
import { trierCandidatsParDate } from './trierCandidatsParDate';

export interface AdversePleadingCandidate {
  id: string;
  name: string;
  /** Backend marker used to hide generated dossier syntheses from source pickers. */
  document_type?: string | null;
  /** Date intrinsèque de la pièce (colonne SQL `date`). Peuplée à ~43 % sur cette catégorie. */
  document_date?: string | null;
}

/** Cache identity mirrors the exact server-enforced candidate scope. */
export function adversePleadingsQueryKey(cabinetId: string | undefined, caseId: string | undefined) {
  return [
    'case-documents',
    cabinetId,
    caseId,
    'partie_adverse',
    'actes_communiques',
    false,
  ] as const;
}

/**
 * Candidate discovery for the Web defense slice.
 *
 * Errors deliberately remain query errors: an unavailable list is not the
 * same business state as a dossier with no adverse pleadings.
 *
 * The list is sorted by intrinsic document date (descending, nulls last) so
 * the most recent adverse pleading appears first in the picker.
 */
export function useAdversePleadingsCandidates(caseId: string | undefined, enabled: boolean) {
  const currentCabinet = useCurrentCabinet();
  const cabinetId = currentCabinet.data?.id;

  return useQuery({
    queryKey: adversePleadingsQueryKey(cabinetId, caseId),
    queryFn: async () => {
      if (!caseId || !cabinetId) {
        throw new Error(
          `Cannot load adverse pleadings: missing server scope cabinetId=${cabinetId ?? '<missing>'}, caseId=${caseId ?? '<missing>'}.`,
        );
      }
      const candidates = await apiClient.get<AdversePleadingCandidate[]>(
        `/cases/${caseId}/documents?category=partie_adverse&subcategory=actes_communiques&is_folder=false`,
      );
      // Syntheses are derived analysis artifacts, not source pleadings. Keep the
      // filter at the read-model boundary so they can never become selectable
      // through the UI, regardless of how the picker is rendered.
      return candidates.filter((candidate) => candidate.document_type !== 'synthesis');
    },
    // `select` runs on every cache read, including pre-seeded Storybook fixtures —
    // keeps the sort consistent whether the data comes from the network or a test.
    select: (data) => trierCandidatsParDate(data),
    enabled: enabled && !!caseId && !!cabinetId,
    staleTime: 30_000,
  });
}
